import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import { ApiClientError } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'

import styles from './AuthPages.module.css'

/** Turn a local SA cellphone into +27… when the user types a leading 0. */
function normalizeWhatsapp(value: string): string {
  const cleaned = value.replace(/[\s()-]/g, '').trim()
  if (!cleaned) return ''
  if (cleaned.startsWith('+')) return cleaned
  if (cleaned.startsWith('0') && cleaned.length >= 10) {
    return `+27${cleaned.slice(1)}`
  }
  if (cleaned.startsWith('27') && cleaned.length >= 11) {
    return `+${cleaned}`
  }
  return cleaned
}

/** Vendor self-registration with email + WhatsApp. */
export function SignupPage() {
  const { register, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    whatsapp_number: '',
    business_name: '',
  })
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  if (isAuthenticated) {
    return <Navigate to="/vendor" replace />
  }

  function update(field: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await register({
        ...form,
        whatsapp_number: normalizeWhatsapp(form.whatsapp_number),
      })
      navigate('/vendor')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Registration failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={styles.page}>
      <div className={styles.panel}>
        <p className={styles.brand}>
          Oikos <span>Market</span>
        </p>
        <h1 className={styles.title}>Vendor signup</h1>
        <p className={styles.lead}>
          All vendors welcome — share your email and WhatsApp so we can keep you in the market loop.
        </p>
        <form className={styles.form} onSubmit={onSubmit}>
          <label className={styles.label}>
            Full name
            <input className={styles.input} value={form.full_name} onChange={(e) => update('full_name', e.target.value)} required />
          </label>
          <label className={styles.label}>
            Business name
            <input className={styles.input} value={form.business_name} onChange={(e) => update('business_name', e.target.value)} />
          </label>
          <label className={styles.label}>
            Username
            <input className={styles.input} value={form.username} onChange={(e) => update('username', e.target.value)} required />
          </label>
          <label className={styles.label}>
            Email
            <input className={styles.input} type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required />
          </label>
          <label className={styles.label}>
            WhatsApp number
            <input
              className={styles.input}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={form.whatsapp_number}
              onChange={(e) => update('whatsapp_number', e.target.value)}
              onBlur={() =>
                update('whatsapp_number', normalizeWhatsapp(form.whatsapp_number) || form.whatsapp_number)
              }
              placeholder="e.g. 082 123 4567"
              required
            />
            <p className={styles.hint}>
              Type your cellphone the usual way, starting with 0 — for example 082 123 4567.
              Spaces are fine. We will save it in the international format automatically.
            </p>
          </label>
          <label className={styles.label}>
            Password
            <input
              className={styles.input}
              type="password"
              value={form.password}
              onChange={(e) => update('password', e.target.value)}
              minLength={6}
              required
            />
          </label>
          {error ? <p className={styles.error}>{error}</p> : null}
          <button className={styles.submit} type="submit" disabled={busy}>
            {busy ? 'Creating account…' : 'Create vendor account'}
          </button>
        </form>
        <p className={styles.footer}>
          Already registered? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </section>
  )
}
